/**
 * Travel Requests View
 *
 * Displays travel expense requests for assistants who need to process
 * travel bookings and reimbursements.
 */

import { renderIcon } from '../utils/icons';
import { createRequestList, type ExpenditureRequest } from '../components/request-list';
import { createSidebarLayout } from '../components/sidebar-layout';
import { exportRequestsToCSV } from '../utils/csv-export';
import { showToast } from '../components/toast';

/**
 * Create the Travel Requests view
 */
export function createTravelRequestsView(): HTMLElement {
  const content = document.createElement('div');
  content.className = 'px-4 lg:px-6 py-6';

  // Travel Requests badge and header
  const header = document.createElement('div');
  header.className = 'mb-6';
  header.innerHTML = `
    <div class="flex items-center gap-2 mb-2">
      <span class="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-blue-500/20 text-blue-400">
        ${renderIcon('plane', { size: 'xs' }).outerHTML}
        Travel Requests
      </span>
    </div>
    <p class="text-sm text-muted-foreground mt-1">Travel expense requests for booking and processing</p>
  `;
  content.appendChild(header);

  // Request list container
  const requestListContainer = document.createElement('div');
  requestListContainer.id = 'request-list-container';
  content.appendChild(requestListContainer);

  // Initialize request list after DOM is ready
  setTimeout(() => {
    let allRequests: ExpenditureRequest[] = [];

    const requestList = createRequestList({
      parent: requestListContainer,
      title: 'Travel Requests',
      subtitle: 'Travel expense requests',
      endpoint: '/requests',
      showFilters: true,
      showSearch: true,
      showStatusColumn: true,
      showCreatorColumn: true,
      emptyMessage: 'No travel requests found.',
      initialFilters: {
        category: 'TRAVEL',
      },
      enableBulkSelection: true,
      showBulkActions: true,
      onBulkExport: (ids: string[]) => {
        const selectedRequests = allRequests.filter(r => ids.includes(r.id));
        exportRequestsToCSV(selectedRequests);
        showToast({
          type: 'success',
          message: `Exported ${selectedRequests.length} request${selectedRequests.length > 1 ? 's' : ''} to CSV`,
        });
      },
      viewId: 'travel-requests',
      showColumnPicker: true,
      hideCategoryFilter: true,
      hiddenColumns: ['category'],
    });

    // Store all requests for export functionality
    const originalMethod = Object.getPrototypeOf(requestList).loadRequests;
    if (originalMethod) {
      Object.getPrototypeOf(requestList).loadRequests = async function(...args: any[]) {
        const result = await originalMethod.apply(this, args);
        allRequests = this.allRequests.length > 0 ? this.allRequests : this.requests;
        return result;
      };
    }
  }, 0);

  return createSidebarLayout(content, {
    currentPath: '/travel-requests',
    title: 'Travel Requests',
    subtitle: 'Process travel bookings and reimbursements',
  });
}

/**
 * Travel Requests view function for router
 */
export function travelRequestsView(): HTMLElement {
  return createTravelRequestsView();
}
